import React, { useCallback, useMemo, useEffect } from 'react'

import { FieldProps, getIn } from 'formik'

import { InputAdornment, CircularProgress } from '@material-ui/core'
import MuiTextField, { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField'

export interface TextFieldProps
  extends FieldProps,
    Omit<MuiTextFieldProps, 'name' | 'value' | 'error'> {}

export function useTextField({
  form,
  field,
  helperText,
  InputProps,
  variant,
  disabled,
  ...props
}: TextFieldProps) {
  const { error, touched, value } = form.getFieldMeta(field.name)
  const status = form.status || {}
  const isLoadingField = getIn(form.status, `${field.name}.loading`)
  const statusError = useMemo(() => getIn(form.status, `${field.name}.error`), [status])

  const fieldError = useMemo(
    () =>
      helperText || // this is to force manually the field state
      ((touched || value) && error) ||
      statusError,
    [statusError, error, touched, value],
  )

  const setError = useCallback(() => {
    if ((statusError || undefined) === error) return
    form.setFieldError(field.name, statusError || null)
  }, [form, statusError])

  useEffect(() => {
    setError()
  }, [statusError])

  return {
    ...props,
    ...field,
    error: !!fieldError,
    helperText: fieldError || '',
    disabled: disabled ?? form.isSubmitting,
    variant,
    InputProps: {
      ...InputProps,
      'data-hook': field.name,
      autoComplete: 'off',
      endAdornment: isLoadingField ? (
        <InputAdornment position="end">
          {isLoadingField && <CircularProgress size={24} />}
        </InputAdornment>
      ) : (
        InputProps?.endAdornment
      ),
    },
  }
}

export default function TextField({ children, ...props }: TextFieldProps) {
  const fieldProps = useTextField(props)
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <MuiTextField {...fieldProps}>{children}</MuiTextField>
}
